// src/utils/payHistory.js
// ===========================================================================
// Pay rates WITH START DATES.
//
// When an employee's or sub's pay changes (Ryan $25/hr -> $28/hr starting
// 2026-11-01), the old rate must still count for every job and day he worked
// before 2026-11-01. So pay changes are kept as a dated history on the person:
//
//   users/{id}.payHistory = [
//     { effectiveFrom: "",           salary: 25, yearlySalary: 52000, ... }  // the rate before any change
//     { effectiveFrom: "2026-11-01", salary: 28, yearlySalary: 58240, ... }
//   ]
//
// The rate for a given day = the LAST entry whose effectiveFrom is on or
// before that day ("" = since the beginning). People with no history are
// unchanged: their current fields are used for every date, as before.
//
// The plain fields on the person (salary, yearlySalary, commissionRate ...)
// always hold the rate in effect TODAY, so screens that only show "the rate"
// keep working. Every labor / pay calculation asks personAtDate(person, day).
// ===========================================================================

/** The fields a pay change covers. */
export const PAY_FIELDS = [
  "salary", // hourly work rate (the app's main pay field)
  "yearlySalary",
  "payCalculationMethod",
  "commissionRate", // default job % (10 = 10%)
  "subDailyRate", // sub contractors: default $ per day per worker
  "clockTimeDailyRate", // Clock Time's daily rate
  "hoursPerDay",
];

export const PAY_FIELD_LABELS = {
  salary: "Hourly rate",
  yearlySalary: "Yearly salary",
  payCalculationMethod: "Pay method",
  commissionRate: "Job %",
  subDailyRate: "Sub $/day/worker",
  clockTimeDailyRate: "Clock Time daily rate",
  hoursPerDay: "Hours per day",
};

export const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Any date-ish value -> "YYYY-MM-DD" ("" when unknown). */
export const toDayKey = (value) => {
  if (!value) return "";
  if (typeof value === "string") return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : "";
  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }
  if (typeof value === "object" && value.seconds) return toDayKey({ toDate: () => new Date(value.seconds * 1000) });
  if (value instanceof Date) return toDayKey({ toDate: () => value });
  return "";
};

const sameValue = (a, b) => String(a ?? "") === String(b ?? "");

/** History sorted oldest -> newest (the "since the beginning" entry first). */
export const sortedPayHistory = (person = {}) =>
  (Array.isArray(person?.payHistory) ? person.payHistory : [])
    .filter((e) => e && typeof e === "object")
    .slice()
    .sort((a, b) => String(a.effectiveFrom || "").localeCompare(String(b.effectiveFrom || "")));

/** The history entry in effect on a day (null when the person has no history). */
export const payEntryAt = (person = {}, day) => {
  const list = sortedPayHistory(person);
  if (!list.length) return null;
  const key = toDayKey(day) || todayKey();
  let found = null;
  list.forEach((e) => {
    if (String(e.effectiveFrom || "") <= key) found = e;
  });
  // A day before the first recorded change -> the earliest entry.
  return found || list[0];
};

/**
 * The person with the pay that was in effect on `day`. Use this everywhere
 * labor or pay is worked out for a job / day. No history -> person unchanged.
 */
export const personAtDate = (person = {}, day) => {
  if (!person) return person;
  const entry = payEntryAt(person, day);
  if (!entry) return person;
  const out = { ...person };
  PAY_FIELDS.forEach((f) => {
    // A field the entry doesn't have did not exist then - don't let today's
    // value leak into an older date.
    if (entry[f] !== undefined) out[f] = entry[f];
    else delete out[f];
  });
  return out;
};

/** The pay fields of a record, as a plain object. */
export const pickPay = (record = {}) => {
  const out = {};
  PAY_FIELDS.forEach((f) => {
    if (record[f] !== undefined && record[f] !== null) out[f] = record[f];
  });
  return out;
};

/** Which pay fields differ between two records. */
export const changedPayFields = (before = {}, after = {}) =>
  PAY_FIELDS.filter((f) => !sameValue(before[f], after[f]) && !(before[f] == null && after[f] === ""));

/**
 * Save a pay change with a start date.
 *   before        the saved record (old pay)
 *   after         the edited record (new pay)
 *   effectiveFrom "YYYY-MM-DD" the new pay starts
 * Returns the fields to write: { payHistory, ...current pay fields }.
 * The current fields become the rate in effect TODAY (a future raise is
 * stored but does not change today's numbers).
 */
export const buildPayChange = ({ before = {}, after = {}, effectiveFrom, changedBy = "", note = "" }) => {
  const start = toDayKey(effectiveFrom) || todayKey();
  let history = sortedPayHistory(before);

  // First change ever: keep the old pay as "since the beginning".
  if (!history.length) {
    history = [{ effectiveFrom: "", ...pickPay(before), note: "Pay before the first dated change", changedAt: new Date().toISOString() }];
  }

  // The new entry carries forward every pay field (the old value where this
  // change didn't touch it) so it is complete on its own.
  const prevEntry = payEntryAt({ payHistory: history }, start) || {};
  const entry = {
    ...pickPay(prevEntry),
    ...pickPay(after),
    effectiveFrom: start,
    changedAt: new Date().toISOString(),
    changedBy,
    note,
  };

  // Replace an entry with the same start date instead of stacking two.
  history = history.filter((e) => String(e.effectiveFrom || "") !== start);
  history.push(entry);
  history.sort((a, b) => String(a.effectiveFrom || "").localeCompare(String(b.effectiveFrom || "")));

  const today = pickPay(payEntryAt({ payHistory: history }, todayKey()) || {});
  return { payHistory: history, ...today };
};

/** Remove one dated change (fix a mistake). Current fields follow. */
export const removePayEntry = (person = {}, effectiveFrom) => {
  const history = sortedPayHistory(person).filter((e) => String(e.effectiveFrom || "") !== String(effectiveFrom || ""));
  if (!history.length) return { payHistory: [] };
  const today = pickPay(payEntryAt({ payHistory: history }, todayKey()) || {});
  return { payHistory: history, ...today };
};

/** "$25.00/hr" style summary of an entry. */
export const describePayEntry = (e = {}) => {
  const parts = [];
  if (Number(e.salary) > 0) parts.push(`$${Number(e.salary).toFixed(2)}/hr`);
  if (Number(e.yearlySalary) > 0) parts.push(`$${Number(e.yearlySalary).toLocaleString("en-US")}/yr`);
  if (e.payCalculationMethod && e.payCalculationMethod !== "hourly_only") parts.push(`${e.payCalculationMethod.replace(/_/g, " ")}`);
  if (Number(e.commissionRate) > 0) parts.push(`${e.commissionRate}% job`);
  if (Number(e.subDailyRate) > 0) parts.push(`$${Number(e.subDailyRate).toFixed(2)}/day/worker`);
  if (Number(e.clockTimeDailyRate) > 0) parts.push(`Clock Time $${Number(e.clockTimeDailyRate).toFixed(2)}/day`);
  return parts.join(" · ") || "No rate";
};
